import { reformatTime, getWeekdayVerbiage } from "@gigpro/utils/datetime";
import type { AvailabilityWindow } from "domains/hire/components/AvailabilityWindowsField";

const dayAbbr = (day: number): string => getWeekdayVerbiage(day + 1);

/** `[0,1,2,3,4]` → "Mon–Fri", `[0,2,4]` → "Mon, Wed, Fri" — contiguous runs collapse to a range. */
export const formatDays = (days: Array<number>): string => {
  const sorted = [...days].toSorted((a, b) => a - b);
  if (sorted.length === 0) return "";
  const runs: Array<string> = [];
  let [start] = sorted;
  let prev = start;
  const flush = () => runs.push(start === prev ? dayAbbr(start) : `${dayAbbr(start)}–${dayAbbr(prev)}`);
  for (const day of sorted.slice(1)) {
    if (day === prev + 1) prev = day;
    else {
      flush();
      start = day;
      prev = day;
    }
  }
  flush();
  return runs.join(", ");
};

/** `"14:00"` → "2:00 pm". Maps the `24:00` end-of-day sentinel to midnight ("12:00 am"). */
export const formatTime = (time: string): string =>
  reformatTime(`${time === "24:00" ? "00:00" : time}:00`, { in: "backend", out: "default" });

export const formatWindow = (window: AvailabilityWindow): string =>
  `${formatDays(window.days)} · ${formatTime(window.start)}–${formatTime(window.end)}`;

/** Read-only rendering of a week, for referencing an inherited default that isn't editable here. */
export function AvailabilityReadout({
  windows,
  emptyLabel = "No availability set.",
}: Readonly<{ windows: Array<AvailabilityWindow>; emptyLabel?: string }>) {
  if (windows.length === 0) return <p className="text-small text-gray">{emptyLabel}</p>;
  return (
    <ul className="flex flex-col gap-1">
      {windows.map((window, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index} className="text-small text-black">
          {formatWindow(window)}
        </li>
      ))}
    </ul>
  );
}
